import React, { useState, useMemo, useEffect } from 'react';
import { api } from '../../src/services/api-new';

interface Props {
    onNavigate: (view: any) => void;
    onBack?: () => void;
    onShowMigration?: () => void;
}

// --- Interfaces ---
interface Pet {
    id: string | number;
    name: string;
    type: string;
    breed: string;
    age: string;
    weight: string;
    status: string;
    statusColor: string;
    image: string;
    medicalId: string;
    lastVisit: string;
}

interface Appointment {
    id: string;
    petName: string;
    petImage: string;
    type: string;
    drName: string;
    date: string;
    time: string;
    status: 'upcoming' | 'completed' | 'cancelled';
}

interface Order {
    id: string;
    date: string;
    items: string;
    total: string;
    status: 'Delivered' | 'In Transit' | 'Processing';
    statusColor: string;
}

const UserDashboard: React.FC<Props> = ({ onNavigate, onBack, onShowMigration }) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'pets' | 'appointments' | 'orders'>('overview');
    const [showPetDetailModal, setShowPetDetailModal] = useState(false);
    const [selectedPet, setSelectedPet] = useState<Pet | null>(null);

    const [pets, setPets] = useState<Pet[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsDataLoading(true);
            try {
                // Fetch all user data from API
                const [patientsData, appointmentsData, ordersData] = await Promise.all([
                    api.patients.getAll(),
                    api.appointments.getAll(),
                    api.orders.getAll()
                ]);

                const mappedPets: Pet[] = patientsData.map((p: any) => ({
                    id: p.id,
                    name: p.name,
                    type: p.type,
                    breed: p.breed,
                    age: p.age,
                    weight: p.weight,
                    status: p.history.length > 0 ? "Healthy" : "Check-up Due",
                    statusColor: p.history.length > 0
                        ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
                        : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
                    image: p.image,
                    medicalId: p.id,
                    lastVisit: p.history?.[0]?.date || "Never"
                }));

                const mappedAppointments: Appointment[] = appointmentsData.map((a: any) => ({
                    id: a.id.toString(),
                    petName: a.patient_name,
                    petImage: mappedPets.find(p => p.name === a.patient_name)?.image || '',
                    type: a.service_name,
                    drName: a.vet_name,
                    date: new Date(a.appointment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    time: new Date(a.appointment_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                    status: a.status === 'Completed' ? 'completed' : 'upcoming'
                }));

                const mappedOrders: Order[] = ordersData.map((o: any) => ({
                    id: o.id.toString(),
                    date: new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                    items: o.item_count ? `${o.item_count} item${o.item_count > 1 ? 's' : ''}` : 'Order items',
                    total: `$${o.total_amount || '0.00'}`,
                    status: o.status === 'Delivered' ? 'Delivered' : o.status === 'In Transit' ? 'In Transit' : 'Processing',
                    statusColor: o.status === 'Delivered' ? 'text-green-500' : o.status === 'In Transit' ? 'text-blue-500' : 'text-yellow-500'
                }));

                setPets(mappedPets);
                setAppointments(mappedAppointments);
                setOrders(mappedOrders);
            } catch (error) {
                console.error("Failed to fetch dashboard data:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleViewDetails = (pet: Pet) => {
        setSelectedPet(pet);
        setShowPetDetailModal(true);
    };

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Loading Personal Hub...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-background-dark p-6 lg:p-10 scroll-smooth">
            <div className="max-w-7xl mx-auto space-y-10">

                {/* Dashboard Header */}
                <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <div>
                            <nav className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                                <span>Dashboard</span>
                                <span className="material-icons-round text-sm">chevron_right</span>
                                <span className="text-primary">{activeTab}</span>
                            </nav>
                            <h1 className="text-4xl font-black text-primary-dark dark:text-white tracking-tight flex items-center gap-3">
                                Hello, Sarah Jenkins <span className="animate-bounce-slow">👋</span>
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 font-bold mt-1 max-w-lg">
                                Your PET-PASS is active. Your records are securely synced with the clinic.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => onShowMigration && onShowMigration()}
                            className="bg-white dark:bg-surface-dark text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-700 hover:border-primary px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-icons-round text-lg">sync</span> Data Migration
                        </button>
                        <button
                            onClick={() => onNavigate('chat')}
                            className="bg-white dark:bg-surface-dark text-primary border-2 border-primary/20 hover:border-primary px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-icons-round text-lg">chat_bubble</span> Consultation
                        </button>
                        <button
                            onClick={() => onNavigate('booking')}
                            className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/30 transition-all flex items-center gap-2 active:scale-95"
                        >
                            <span className="material-icons-round text-lg">add_circle</span> Book Slot
                        </button>
                    </div>
                </header>

                {/* Tabs */}
                <div className="flex bg-white dark:bg-surface-dark/50 backdrop-blur-md p-1.5 rounded-[1.5rem] shadow-sm border border-gray-100 dark:border-gray-800 w-fit overflow-x-auto no-scrollbar max-w-full">
                    <Tab label="Overview" icon="dashboard" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
                    <Tab label="My Pets" icon="pets" active={activeTab === 'pets'} onClick={() => setActiveTab('pets')} />
                    <Tab label="Appointments" icon="calendar_month" active={activeTab === 'appointments'} onClick={() => setActiveTab('appointments')} />
                    <Tab label="Orders" icon="shopping_bag" active={activeTab === 'orders'} onClick={() => setActiveTab('orders')} />
                </div>

                {/* Content Area */}
                <div className="animate-fade-in-up">
                    {activeTab === 'overview' && <OverviewTab pets={pets} appointments={appointments} onViewDetails={handleViewDetails} />}
                    {activeTab === 'pets' && <PetsTab pets={pets} onViewDetails={handleViewDetails} />}
                    {activeTab === 'appointments' && <AppointmentsTab appointments={appointments} />}
                    {activeTab === 'orders' && <OrdersTab orders={orders} />}
                </div>
            </div>

            {/* Detail Modal */}
            {showPetDetailModal && selectedPet && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/40 backdrop-blur-md animate-fade-in" onClick={() => setShowPetDetailModal(false)}>
                    <div className="bg-white/95 dark:bg-surface-dark/95 backdrop-blur-xl w-full max-w-xl rounded-[2.5rem] shadow-2xl p-10 relative overflow-hidden animate-scale-up-md border border-white/20 dark:border-gray-800" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-8">
                            <div className="flex gap-6 items-center">
                                <img src={selectedPet.image} className="w-24 h-24 rounded-3xl object-cover shadow-xl" alt={selectedPet.name} />
                                <div>
                                    <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tighter">{selectedPet.name}</h2>
                                    <p className="text-primary font-bold text-xs uppercase tracking-widest mt-1">{selectedPet.breed}</p>
                                    <div className={`mt-3 py-1 px-3 rounded-full text-[10px] font-black uppercase inline-block border ${selectedPet.statusColor}`}>
                                        {selectedPet.status}
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setShowPetDetailModal(false)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors flex items-center justify-center">
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mb-10">
                            <MetricCard icon="scale" label="Weight" value={selectedPet.weight} />
                            <MetricCard icon="cake" label="Age" value={selectedPet.age} />
                            <MetricCard icon="fingerprint" label="PET-ID" value={selectedPet.medicalId} />
                            <MetricCard icon="history" label="Last Seen" value={selectedPet.lastVisit} />
                        </div>

                        <button className="w-full py-5 bg-primary text-white font-black rounded-2xl hover:bg-primary-dark transition-all shadow-xl shadow-primary/30 uppercase tracking-widest text-sm flex items-center justify-center gap-2 active:scale-95">
                            <span className="material-icons-round">medical_information</span>
                            Download Medical History
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Sub-components ---

const Tab = ({ label, icon, active, onClick }: any) => (
    <button onClick={onClick} className={`flex items-center gap-2 px-6 py-3 rounded-2xl transition-all whitespace-nowrap font-black text-xs uppercase tracking-widest ${active ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}>
        <span className="material-icons-round text-lg">{icon}</span>
        {label}
    </button>
);

const MetricCard = ({ icon, label, value }: any) => (
    <div className="bg-gray-50/50 dark:bg-background-dark/50 p-5 rounded-[1.5rem] border border-gray-100 dark:border-gray-800 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <span className="material-icons-round text-xl">{icon}</span>
        </div>
        <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
            <p className="font-black text-gray-900 dark:text-white">{value}</p>
        </div>
    </div>
)

const OverviewTab = ({ pets, appointments, onViewDetails }: any) => {
    const nextApp = appointments[0];
    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-10">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                    <StatCard label="My Pets" value={pets.length} icon="pets" color="blue" />
                    <StatCard label="Bookings" value={appointments.filter((a: any) => a.status === 'upcoming').length} icon="event" color="purple" />
                    <StatCard label="Treats" value={2} icon="shopping_bag" color="orange" />
                    <StatCard label="Savings" value="$120" icon="savings" color="green" />
                </div>

                {/* My Pets Preview */}
                <section className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Family Members</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                        {pets.map((pet: any) => (
                            <div key={pet.id} onClick={() => onViewDetails(pet)} className="group bg-white dark:bg-surface-dark rounded-[2rem] p-6 border border-gray-100 dark:border-gray-800 hover:border-primary/50 hover:shadow-2xl transition-all cursor-pointer flex gap-5 items-center">
                                <img src={pet.image} className="w-20 h-20 rounded-2xl object-cover shadow-lg group-hover:scale-105 transition-transform" alt={pet.name} />
                                <div className="flex-1">
                                    <h3 className="font-black text-xl text-primary-dark dark:text-white">{pet.name}</h3>
                                    <p className="text-sm text-gray-400 font-bold">{pet.breed}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className={`w-2 h-2 rounded-full ${pet.statusColor.includes('green') ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{pet.status}</span>
                                    </div>
                                </div>
                                <span className="material-icons-round text-gray-200 group-hover:text-primary group-hover:translate-x-1 transition-all">arrow_forward</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* Right Col: Timeline/Next Event */}
            <div className="lg:col-span-4 space-y-10">
                {nextApp && (
                    <div className="bg-gradient-to-br from-primary-dark to-[#0F172A] rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-2xl">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-primary/20 rounded-full blur-3xl -mr-12 -mt-12"></div>
                        <div className="relative z-10">
                            <span className="bg-primary text-white text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-[0.2em] shadow-lg shadow-primary/30">Next Appointment</span>
                            <div className="flex items-end gap-3 mb-2 mt-8">
                                <h3 className="text-6xl font-black tracking-tighter">{nextApp.date.split(' ')[1].slice(0, -1)}</h3>
                                <span className="text-2xl font-bold opacity-60 mb-2 uppercase">{nextApp.date.split(' ')[0]}</span>
                            </div>
                            <p className="text-gray-400 font-bold text-sm mb-8 flex items-center gap-2 uppercase tracking-widest">
                                <span className="material-icons-round text-sm">schedule</span> {nextApp.time}
                            </p>

                            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10 space-y-4">
                                <div className="flex items-center gap-4">
                                    <img src={nextApp.petImage} className="w-12 h-12 rounded-xl object-cover ring-2 ring-primary/30" alt="pet" />
                                    <div>
                                        <p className="font-black text-lg leading-tight">{nextApp.petName}</p>
                                        <p className="text-xs text-primary font-bold uppercase tracking-wider">{nextApp.type}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xs font-black">DR</div>
                                    <div><p className="text-xs text-gray-400 font-bold uppercase">Vet In Charge</p><p className="font-bold text-sm tracking-tight">{nextApp.drName}</p></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-white dark:bg-surface-dark rounded-[2.5rem] p-8 border border-gray-100 dark:border-gray-800 space-y-6 shadow-sm">
                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Health Tip of the Day</h4>
                    <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center text-yellow-500 shrink-0">
                            <span className="material-icons-round">lightbulb</span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium leading-relaxed">
                            Ensure your pet stays hydrated during the warm months. Fresh water and shade are essential for all furry friends!
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, icon, color }: any) => {
    const colors: any = {
        blue: 'text-blue-500 bg-blue-500/10 dark:bg-blue-500/5 hover:border-blue-500/30',
        purple: 'text-purple-500 bg-purple-500/10 dark:bg-purple-500/5 hover:border-purple-500/30',
        orange: 'text-orange-500 bg-orange-500/10 dark:bg-orange-500/5 hover:border-orange-500/30',
        green: 'text-green-500 bg-green-500/10 dark:bg-green-500/5 hover:border-green-500/30',
    };
    return (
        <div className={`bg-white dark:bg-surface-dark p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm transition-all hover:shadow-xl group`}>
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110 ${colors[color]}`}>
                <span className="material-icons-round text-2xl">{icon}</span>
            </div>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">{label}</p>
            <p className="text-3xl font-black text-primary-dark dark:text-white tracking-tighter">{value}</p>
        </div>
    );
}

const PetsTab = ({ pets, onViewDetails }: any) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {pets.map((pet: any) => (
            <div key={pet.id} onClick={() => onViewDetails(pet)} className="group bg-white dark:bg-surface-dark rounded-[2.5rem] overflow-hidden border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:shadow-2xl transition-all cursor-pointer flex flex-col">
                <div className="aspect-[4/3] w-full relative overflow-hidden bg-gray-100">
                    <img src={pet.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={pet.name} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-8">
                        <span className="text-white font-black text-xs uppercase tracking-widest flex items-center gap-2">
                            <span className="material-icons-round text-sm">visibility</span> View Medical Files
                        </span>
                    </div>
                    <div className={`absolute top-6 right-6 text-[10px] font-black px-4 py-2 rounded-full border shadow-xl backdrop-blur-md ${pet.statusColor}`}>{pet.status}</div>
                </div>
                <div className="p-8 pb-10">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-black text-2xl text-primary-dark dark:text-white tracking-tight">{pet.name}</h3>
                        <span className="material-icons-round text-gray-200 group-hover:text-primary">pets</span>
                    </div>
                    <p className="text-sm text-gray-400 font-bold uppercase tracking-wider">{pet.type} • {pet.breed}</p>
                    <div className="flex gap-4 mt-6">
                        <div className="flex-1 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-center">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Age</p>
                            <p className="font-black text-sm">{pet.age}</p>
                        </div>
                        <div className="flex-1 py-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-center">
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Weight</p>
                            <p className="font-black text-sm">{pet.weight}</p>
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const AppointmentsTab = ({ appointments }: any) => (
    <div className="space-y-6">
        <h2 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Visit Timeline</h2>
        <div className="space-y-4">
            {appointments.map((app: any) => (
                <div key={app.id} className="bg-white dark:bg-surface-dark rounded-3xl p-6 border border-gray-100 dark:border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-6">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${app.status === 'upcoming' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'}`}>
                            <span className="material-icons-round text-2xl">{app.status === 'upcoming' ? 'pending_actions' : 'task_alt'}</span>
                        </div>
                        <div>
                            <h3 className="font-black text-lg">{app.type}</h3>
                            <p className="text-sm text-gray-400 font-bold">{app.petName} • {app.drName}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-10">
                        <div className="text-right">
                            <p className="font-black text-sm">{app.date}</p>
                            <p className="text-xs text-gray-400 font-bold uppercase">{app.time}</p>
                        </div>
                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase border ${app.status === 'upcoming' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 'bg-gray-500/10 text-gray-500 border-gray-500/10'}`}>
                            {app.status}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const OrdersTab = ({ orders }: any) => (
    <div className="space-y-6">
        <h2 className="text-2xl font-black text-primary-dark dark:text-white tracking-tight">Shopping History</h2>
        <div className="grid gap-4">
            {orders.map((order: any) => (
                <div key={order.id} className="bg-white dark:bg-surface-dark rounded-3xl p-8 border border-gray-100 dark:border-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex gap-6 items-center">
                        <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-gray-400 shadow-inner">
                            <span className="material-icons-round text-3xl">shopping_cart</span>
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{order.id}</p>
                            <h4 className="font-black text-lg">{order.items}</h4>
                            <p className="text-sm font-bold text-gray-400">Purchased on {order.date}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-12">
                        <div className="text-right">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Price</p>
                            <p className="text-2xl font-black text-primary">{order.total}</p>
                        </div>
                        <div className="text-right min-w-[100px]">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</p>
                            <p className={`font-black text-sm ${order.statusColor}`}>{order.status}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default UserDashboard;
